/**
 * RemixLoopCTA — the post-session "ritual" CTA that turns a one-off
 * demo into a weekly habit.
 *
 * Two modes (driven by whether `onPlayAnother` was passed):
 *   • EXPLAINER mode (no onPlayAnother) — for the public demos. Just
 *     describes the Smart Shuffle ritual as a FEATURE preview so
 *     viewers understand what families would set up. The "Play another
 *     session" button is hidden because cycling back through Margaret's
 *     intro four times is painful UX in a guided tour.
 *   • INTERACTIVE mode (onPlayAnother passed) — for real authenticated
 *     family caregivers. Includes the "Play another session" CTA that
 *     mints a new seed + fresh=true.
 *
 * Both modes include the optional "Get a weekly reminder" capture so
 * we can build the SMS subscriber list ahead of the Twilio wiring.
 */
import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Repeat, BellRinging, Check, X } from "@phosphor-icons/react";
import { API } from "../lib/api";
import { toast } from "sonner";

const publicApi = axios.create({ baseURL: API, withCredentials: false });

const CADENCE_OPTIONS = [
    { id: "weekly_saturday_9am",  label: "Saturday mornings"  },
    { id: "weekly_sunday_10am",   label: "Sunday mornings"    },
    { id: "weekly_wednesday_3pm", label: "Wednesday afternoons" },
    { id: "daily_9am",            label: "Every morning"      },
];

export function RemixLoopCTA({
    slug,
    visitorToken,
    onPlayAnother,         // when undefined / null, render in EXPLAINER mode
    theme = "dark",
    subjectName,
}) {
    const isDark = theme !== "light";
    const interactive = typeof onPlayAnother === "function";

    const [showReminder, setShowReminder] = useState(false);
    const [phone, setPhone] = useState("");
    const [cadence, setCadence] = useState("weekly_saturday_9am");
    const [submitting, setSubmitting] = useState(false);
    const [subscribed, setSubscribed] = useState(false);

    const sendReminder = async (e) => {
        e?.preventDefault?.();
        if (submitting) return;
        setSubmitting(true);
        try {
            const cleaned = phone.replace(/[\s-]/g, "");
            const { data } = await publicApi.post(
                `/demo/${slug}/reminders`,
                {
                    visitor_token: visitorToken,
                    phone_e164: cleaned,
                    cadence,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Australia/Melbourne",
                }
            );
            setSubscribed(true);
            toast.success(`We'll text ${data.phone_e164} ${humanCadence(cadence)} once SMS goes live.`);
        } catch (err) {
            const msg = err?.response?.data?.detail || "Couldn't save that number — try +<country><digits>";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const wrap = isDark
        ? "bg-white/[0.05] border border-white/12 text-white"
        : "bg-white/60 border border-[#C97B8E]/15 text-[#3A2A30]";
    const primaryBtn = isDark
        ? "bg-[#A8B5E0] text-[#1a1c25] hover:bg-[#B8C5F0]"
        : "bg-[#C97B8E] text-white hover:bg-[#B86A7E]";
    const secondaryBtn = isDark
        ? "border-white/25 text-white/80 hover:text-white hover:border-white/50"
        : "border-[#C97B8E]/30 text-[#3A2A30]/75 hover:text-[#3A2A30] hover:border-[#C97B8E]/55";
    const subText = isDark ? "text-white/60" : "text-[#3A2A30]/65";

    // Different headline / body depending on mode.
    const headline = interactive
        ? "Press play again next Saturday."
        : "Built for the long haul.";
    const body = interactive
        ? `Smart shuffle remembers what ${subjectName || "they"} just heard, so each visit surfaces fresh memories first. A weekly ritual, not a one-off tour.`
        : `In a real ${subjectName || "remembr"} session, families build up dozens or hundreds of memories across decades. Smart shuffle picks fresh moments every Saturday, mixes the voices of the people ${subjectName || "they"} love, and caps the length to 15, 30 or 60 minutes — so every visit feels new and never overstays its welcome.`;

    return (
        <div
            className={`max-w-2xl mx-auto rounded-3xl p-6 sm:p-7 backdrop-blur ${wrap}`}
            data-testid="remix-loop-cta"
        >
            <p className={`text-[10px] uppercase tracking-[0.32em] mb-3 ${isDark ? "text-[#A8B5E0]" : "text-[#C97B8E]"}`}>
                {interactive ? "Next visit" : "Smart shuffle"}
            </p>
            <h3 className="font-serif italic text-2xl sm:text-3xl mb-2 leading-tight">
                {headline}
            </h3>
            <p className={`text-sm sm:text-base ${subText} mb-6`}>
                {body}
            </p>

            {/* Action row */}
            <div className="flex flex-col sm:flex-row gap-3">
                {interactive && (
                    <button
                        type="button"
                        onClick={() => onPlayAnother?.()}
                        className={`flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-medium text-sm transition-all ${primaryBtn}`}
                        data-testid="remix-play-another"
                    >
                        <Repeat size={18} weight="bold" />
                        Play another session
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => setShowReminder((v) => !v)}
                    className={`flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-medium text-sm border transition-all ${interactive ? secondaryBtn : primaryBtn}`}
                    data-testid="remix-toggle-reminder"
                >
                    <BellRinging size={18} weight={interactive ? "regular" : "bold"} />
                    {interactive ? "Get a weekly reminder" : "Remind me when remembr is live"}
                </button>
            </div>

            {/* Reminder capture — opens inline */}
            <AnimatePresence>
                {showReminder && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="overflow-hidden"
                    >
                        <div className={`mt-5 pt-5 border-t ${isDark ? "border-white/10" : "border-[#C97B8E]/15"}`}>
                            {!subscribed ? (
                                <form onSubmit={sendReminder} className="space-y-3" data-testid="remix-reminder-form">
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <input
                                            type="tel"
                                            inputMode="tel"
                                            required
                                            placeholder="+61 400 000 000"
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                            className={`flex-1 px-4 py-2.5 rounded-full text-sm outline-none ${isDark
                                                ? "bg-white/95 text-[#2D2B2A] placeholder-[#8A8786] focus:bg-white"
                                                : "bg-white/95 text-[#2D2B2A] placeholder-[#8A8786] focus:bg-white border border-[#C97B8E]/30"}`}
                                            data-testid="remix-reminder-phone"
                                        />
                                        <select
                                            value={cadence}
                                            onChange={(e) => setCadence(e.target.value)}
                                            className={`px-3 py-2.5 rounded-full text-sm outline-none ${isDark
                                                ? "bg-white/95 text-[#2D2B2A]"
                                                : "bg-white/95 text-[#2D2B2A] border border-[#C97B8E]/30"}`}
                                            data-testid="remix-reminder-cadence"
                                        >
                                            {CADENCE_OPTIONS.map((o) => (
                                                <option key={o.id} value={o.id}>{o.label}</option>
                                            ))}
                                        </select>
                                        <button
                                            type="submit"
                                            disabled={submitting || phone.trim().length < 6}
                                            className={`px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all disabled:opacity-50 ${primaryBtn}`}
                                            data-testid="remix-reminder-submit"
                                        >
                                            {submitting ? "Saving…" : "Save"}
                                        </button>
                                    </div>
                                    <p className={`text-[11px] ${subText}`}>
                                        We'll text you when {subjectName || "your loved one"}'s next session is queued. No marketing, just the link. SMS goes live once we connect Twilio.
                                    </p>
                                </form>
                            ) : (
                                <div className="flex items-start gap-3" data-testid="remix-reminder-saved">
                                    <Check size={20} weight="bold" className={isDark ? "text-[#A8B5E0] mt-0.5" : "text-[#C97B8E] mt-0.5"} />
                                    <div>
                                        <p className="text-sm font-medium">You're on the list.</p>
                                        <p className={`text-xs ${subText} mt-0.5`}>
                                            {humanCadence(cadence)} we'll send you a one-tap link to {subjectName || "their"} next session.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={() => { setSubscribed(false); setShowReminder(false); setPhone(""); }}
                                            className={`mt-2 text-[11px] uppercase tracking-[0.24em] ${subText} hover:opacity-80 inline-flex items-center gap-1`}
                                            data-testid="remix-reminder-edit"
                                        >
                                            <X size={12} /> Change number
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

function humanCadence(id) {
    return (CADENCE_OPTIONS.find((o) => o.id === id)?.label || "Each week").toLowerCase();
}
